/**
 * Runtime Config Revert Button
 *
 * Drops an owned config copy at this context level so it goes back to
 * inheriting from a broader level. Only ever rendered for an owned,
 * non-global context — extracted to keep RuntimeConfigDialog under the
 * 200-line component cap.
 */

import type { ReactElement } from 'react';
import { useRevertRuntimeConfig } from '../../hooks/useRuntimeConfig';
import type { RuntimeConfigCtx } from '../../api/adminApi';

interface RuntimeConfigRevertButtonProps {
  featureId: string;
  featureName: string;
  contextLevel: 'global' | 'pack' | 'project';
  ctx: RuntimeConfigCtx;
  setApiError: (message: string | null) => void;
}

export function RuntimeConfigRevertButton({
  featureId,
  featureName,
  contextLevel,
  ctx,
  setApiError,
}: RuntimeConfigRevertButtonProps): ReactElement {
  const revert = useRevertRuntimeConfig();

  const handleRevert = (): void => {
    if (!window.confirm(
      `Revert this ${contextLevel}'s copy of "${featureName}"'s config? It will go back to inheriting from a broader level.`,
    )) return;
    setApiError(null);
    revert.mutate(
      { featureId, ctx },
      { onError: () => { setApiError(`Failed to revert "${featureName}"'s config at the ${contextLevel} level.`); } },
    );
  };

  return (
    <button
      type="button"
      className="dialog__btn dialog__btn--danger"
      disabled={revert.isPending}
      onClick={handleRevert}
    >
      {revert.isPending ? 'Reverting…' : 'Revert'}
    </button>
  );
}
